import React from 'react'
import { styled } from '@mui/material/styles'
import DescriptionIcon from '@mui/icons-material/Description'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import Checkbox from '../formControls/Checkbox'
import queryKeys from '../../services/queryKeys'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { useLoanContext } from '../../contexts/LoanContext'
import { IMAGE_EXTENSIONS } from './utils/constants'
import { formatDate } from '../../services/helper'
import { MediaFileProps } from './types'
import ImageSkeleton from '../skeletons/ImageSkeleton'

export type MediaLibraryCardProps = {
	media: MediaFileProps
	onSelect?: (media: MediaFileProps) => void
	selected?: boolean
	bulkSelect?: boolean
	loading?: boolean
}

const UncheckedIcon = styled('span')(() => ({
	borderRadius: 3,
	width: 18,
	height: 18,
	backgroundColor: '#fff',
	border: '1px solid #000',
	margin: '2.5px',
}))

const MediaLibraryCard = ({
	media,
	onSelect,
	selected,
	bulkSelect,
	loading = false,
}: MediaLibraryCardProps) => {
	const { id, createdAt, name, extension, url } = media
	const { loanId } = useLoanContext()
	const { formatMessage } = useIntl()

	const isImage = !!(
		extension && IMAGE_EXTENSIONS.includes(extension)
	)

	const { data: base64Data, isFetching: isFetchingBase64Data } =
		useQuery({
			queryKey: [queryKeys.loanDocumentContent, id],
			queryFn: () =>
				TheBigPOSApi.getLoanDocumentContent(loanId, id, {
					contentType: 'base64',
				}),
			enabled: !!id && !!loanId && isImage,
			select: (res) => res.data,
		})

	const title = name
	const altText = name

	const base64Src = base64Data
		? `data:application/${extension};base64,${base64Data}`
		: undefined

	const image = url ?? base64Src ?? false

	const handleSelect = () => {
		onSelect?.({
			...media,
			base64Src,
		})
	}

	const imageBgClass =
		'absolute top-0 left-0 w-full h-full object-cover flex justify-center items-center'

	const isLoading = loading || isFetchingBase64Data

	if (!id) return null
	return (
		<div className="relative h-full" data-cy="media-library-card">
			<button
				type="button"
				onClick={handleSelect}
				className={clsx(
					'overflow-hidden cursor-pointer w-full border border-black dark:border-white border-opacity-[0.12] dark:border-opacity-[0.12] rounded bg-white dark:bg-[#121212] relative flex flex-col h-full transition-all duration-300',
					selected
						? 'shadow-[0px_0px_10px_rgba(48,48,48,0.2)] dark:shadow-gray-400/40'
						: 'hover:shadow-[0px_0px_10px_rgba(48,48,48,0.2)] dark:hover:shadow-gray-400/40'
				)}
			>
				<div className="relative pt-[80%] w-full">
					<div
						className={clsx(
							imageBgClass,
							(!isImage || !image) &&
								!isFetchingBase64Data &&
								'bg-slate-300 text-white dark:bg-gray-700 dark:text-gray-600'
						)}
					>
						{isLoading ? (
							<ImageSkeleton />
						) : (
							<>
								{isImage && !image && (
									<ImageNotSupportedIcon fontSize="large" />
								)}
								{isImage && !!image && (
									<img
										className={imageBgClass}
										src={image}
										alt={altText}
									/>
								)}
								{!isImage && <DescriptionIcon fontSize="large" />}
							</>
						)}
					</div>
				</div>
				<div className="flex-1 relative pt-20 border-t border-black dark:border-white border-opacity-[0.12] dark:border-opacity-[0.12]">
					<div className="absolute p-4 top-0 left-0 w-full h-full">
						<span className="block font-bold font-rubik text-xs sm:text-sm dark:text-slate-200 overflow-hidden whitespace-nowrap text-ellipsis">
							{title ?? formatMessage({ id: 'global.untitled' })}
						</span>
						<span className="block font-rubik !text-sm text-slate-500 sm:text-base dark:text-slate-300  overflow-hidden whitespace-nowrap text-ellipsis">
							{formatDate(createdAt)}
						</span>
					</div>
				</div>
				{bulkSelect && (
					<div className="absolute top-1 left-3 text-black">
						<Checkbox
							size="medium"
							checked={selected}
							icon={<UncheckedIcon />}
						/>
					</div>
				)}
			</button>
		</div>
	)
}

export default MediaLibraryCard
